import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { AuthContext } from '../context/AuthContext';
import { useLanguageCurrency } from '../context/LanguageCurrencyContext';
import api from '../utils/api';
import { mapBackendProperty } from '../utils/propertyMapper';
import DateFieldWithCalendar from '../components/DateFieldWithCalendar';
import { hasPartialPackageSelection } from '../components/BookingCalendar';

const toYMD = (d) => {
  const date = new Date(d);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

/** Package nights = first to last date EXCLUSIVE (last = check-out). */
function getPackageNightSet(property, pkg) {
  const dates = pkg?.selectedDates;
  if (!Array.isArray(dates) || dates.length === 0) return new Set();
  const ymds = dates.map((d) => toYMD(new Date(d))).filter(Boolean).sort();
  if (ymds.length === 0) return new Set();
  const set = new Set();
  const start = new Date(ymds[0] + 'T12:00:00');
  const end = new Date(ymds[ymds.length - 1] + 'T12:00:00');
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) set.add(toYMD(d));
  return set;
}

/** Base price: full package = package price; outside only = nights × per-night; mixed = package price + (extra nights × per-night). */
function getStayBasePrice(property, checkIn, checkOut) {
  if (!property || !checkIn || !checkOut) return 0;
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const stayDates = new Set();
  for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) stayDates.add(toYMD(d));

  const perNight = property.price ?? 0;
  const packages = property.packages || [];

  for (const pkg of packages) {
    if (!pkg.isActive || !pkg.selectedDates?.length) continue;
    const pkgNights = getPackageNightSet(property, pkg);
    if (pkgNights.size === 0) continue;
    const overlap = [...pkgNights].filter((ymd) => stayDates.has(ymd));
    if (overlap.length === 0) continue;
    if (overlap.length === pkgNights.size) {
      const packagePrice = pkg.price ?? 0;
      if (stayDates.size === pkgNights.size) return packagePrice;
      const extraNights = stayDates.size - pkgNights.size;
      return packagePrice + extraNights * perNight;
    }
  }

  return stayDates.size * perNight;
}

const PropertyDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const { formatPrice, language } = useLanguageCurrency();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingForm, setBookingForm] = useState({
    checkIn: '',
    checkOut: '',
    guests: 1,
  });
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [mediaItems, setMediaItems] = useState([]);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [error, setError] = useState('');
  const [showPartialPackageModal, setShowPartialPackageModal] = useState(false);

  useEffect(() => {
    fetchProperty();
  }, [id, language]);

  useEffect(() => {
    const propertyId = property?.id ?? property?._id;
    if (!propertyId) return;
    let cancelled = false;
    api.get('/api/bookings/available-dates', { params: { propertyId } })
      .then((res) => {
        if (!cancelled && res.data?.blockedDates) setBlockedDates(res.data.blockedDates);
      })
      .catch(() => { if (!cancelled) setBlockedDates([]); });
    return () => { cancelled = true; };
  }, [property?.id, property?._id]);

  useEffect(() => {
    if (location.state?.bookingData && property) {
      const data = location.state.bookingData;
      setBookingForm((prev) => ({
        ...prev,
        checkIn: data.checkIn || prev.checkIn,
        checkOut: data.checkOut || prev.checkOut,
        guests: data.guests ?? prev.guests
      }));
    }
  }, [location.state?.bookingData, property]);

  const fetchProperty = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/api/properties/${id}`);
      const raw = response.data?.property;
      if (!raw) {
        setError(t('common.propertyNotFound'));
        setProperty(null);
        setLoading(false);
        return;
      }
      const foundProperty = mapBackendProperty(raw, language || 'en');
      if (!foundProperty.mapLink && (foundProperty.address || foundProperty.location)) {
        foundProperty.mapLink = `https://www.google.com/maps?q=${encodeURIComponent(foundProperty.address || foundProperty.location)}`;
      }
      setProperty(foundProperty);
      const propertyImages = foundProperty.images && foundProperty.images.length > 0
        ? foundProperty.images
        : (foundProperty.image ? [foundProperty.image] : []);
      setImages(propertyImages);
      const propertyVideos = foundProperty.videos || (foundProperty.video ? [foundProperty.video] : []);
      setVideos(propertyVideos);
      const combinedMedia = [
        ...propertyImages.map(img => ({ type: 'image', url: img })),
        ...propertyVideos.map(video => ({ type: 'video', url: video }))
      ];
      setMediaItems(combinedMedia);
      setCurrentMediaIndex(0);
    } catch (err) {
      console.error('Error fetching property:', err);
      setError(err.response?.status === 404 ? t('common.propertyNotFound') : t('common.errorLoadingProperty'));
      setProperty(null);
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!user) {
      navigate('/login', { state: { redirectTo: `/properties/${id}`, redirectView: 'booking' } });
      return;
    }

    if (!bookingForm.checkIn || !bookingForm.checkOut) {
      setError(t('common.selectDates'));
      return;
    }

    const checkIn = new Date(bookingForm.checkIn);
    const checkOut = new Date(bookingForm.checkOut);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (checkIn < today) {
      setError(t('common.checkInPastError'));
      return;
    }

    if (checkOut <= checkIn) {
      setError(t('common.checkOutAfterCheckInError'));
      return;
    }

    if (bookingForm.guests < 1 || bookingForm.guests > 50) {
      setError(t('propertyDetail.guestsRangeError'));
      return;
    }

    if (property?.packages?.length && hasPartialPackageSelection(property.packages, bookingForm.checkIn, bookingForm.checkOut)) {
      setShowPartialPackageModal(true);
      return;
    }

    // Navigate to booking page with property and booking data
    navigate('/booking', {
      state: {
        property: property,
        bookingData: bookingForm
      }
    });
  };

  const nextMedia = () => {
    setCurrentMediaIndex((prev) => (prev + 1) % mediaItems.length);
  };

  const prevMedia = () => {
    setCurrentMediaIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
  };

  const isVideo = (url) => {
    return url.includes('youtube.com') || url.includes('youtu.be') || url.includes('vimeo.com') || url.endsWith('.mp4') || url.endsWith('.webm') || url.endsWith('.mov');
  };

  const getVideoEmbedUrl = (url) => {
    if (url.includes('youtube.com/watch')) {
      const videoId = url.split('v=')[1]?.split('&')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('youtu.be/')) {
      const videoId = url.split('youtu.be/')[1]?.split('?')[0];
      return `https://www.youtube.com/embed/${videoId}`;
    } else if (url.includes('vimeo.com')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }
    return url; // Direct video URL
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: '#D5B577' }}></div>
            <p className="text-gray-300">{t('common.loadingPropertyDetails')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B' }}>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">{t('common.propertyNotFound')}</h2>
            <button
              onClick={() => navigate('/properties')}
              className="px-6 py-3 font-semibold rounded-xl transition-all duration-200"
              style={{ backgroundColor: '#D5B577', color: '#1D2A3B' }}
            >
              {t('common.backToProperties')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate nights dynamically
  const calculateNights = () => {
    if (!bookingForm.checkIn || !bookingForm.checkOut) return 0;
    const checkIn = new Date(bookingForm.checkIn);
    const checkOut = new Date(bookingForm.checkOut);
    if (checkOut <= checkIn) return 0;
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const nights = calculateNights();
  const basePrice = nights > 0 && property ? getStayBasePrice(property, bookingForm.checkIn, bookingForm.checkOut) : 0;
  const guestsCount = Number(bookingForm.guests) || 1;
  const includedGuests = property?.maxGuests != null ? property.maxGuests : 1;
  const extraFeesPerPerson = Number(property?.extraFeesPerPerson) || 0;
  const extraGuestsFee = guestsCount > includedGuests && nights > 0
    ? (guestsCount - includedGuests) * extraFeesPerPerson * nights
    : 0;
  const totalPrice = basePrice + extraGuestsFee;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1D2A3B', width: '100%' }}>
      <Navbar />

      {/* Property Media Gallery */}
      <section className="relative pt-16" style={{ width: '100%', minHeight: 'auto' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main hero + thumbnails grid: hero left, thumbnails right on desktop */}
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-4">
            {/* Main Media Display */}
            <div className="relative flex-1 min-h-[280px] sm:min-h-[360px] md:min-h-[420px] lg:min-h-[480px] rounded-2xl overflow-hidden bg-black shadow-2xl">
              {mediaItems.length > 0 && (
                <>
                  <div className="absolute inset-0 transition-opacity duration-300" key={currentMediaIndex}>
                    {mediaItems[currentMediaIndex].type === 'video' || isVideo(mediaItems[currentMediaIndex].url) ? (
                      <div className="w-full h-full flex items-center justify-center bg-black">
                        {mediaItems[currentMediaIndex].url.includes('youtube.com') || mediaItems[currentMediaIndex].url.includes('youtu.be') || mediaItems[currentMediaIndex].url.includes('vimeo.com') ? (
                          <iframe
                            src={getVideoEmbedUrl(mediaItems[currentMediaIndex].url)}
                            className="w-full h-full min-h-[280px]"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title={property.title}
                          />
                        ) : (
                          <video
                            src={mediaItems[currentMediaIndex].url}
                            className="w-full h-full object-contain"
                            controls
                            playsInline
                            poster={property.image || (images[0])}
                            preload="metadata"
                          >
                            {t('common.videoNotSupported')}
                          </video>
                        )}
                      </div>
                    ) : (
                      <img
                        src={mediaItems[currentMediaIndex].url}
                        alt={property.title}
                        className="w-full h-full object-cover cursor-zoom-in"
                        onClick={() => setIsLightboxOpen(true)}
                      />
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none rounded-2xl" />
                  {/* Counter + type badge */}
                  <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-10">
                    <span className="bg-black/60 backdrop-blur-md rounded-full px-3 py-1.5 text-white text-sm font-medium">
                      {currentMediaIndex + 1} / {mediaItems.length}
                    </span>
                    <span className="bg-black/60 backdrop-blur-md rounded-full px-3 py-1.5 flex items-center gap-2 text-white text-xs font-medium">
                      {mediaItems[currentMediaIndex].type === 'video' || isVideo(mediaItems[currentMediaIndex].url) ? (
                        <><svg className="w-4 h-4" style={{ color: '#D5B577' }} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>{t('common.video')}</>
                      ) : (
                        <><svg className="w-4 h-4" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14" /></svg>{t('common.photo')}</>
                      )}
                    </span>
                  </div>
                  {/* Nav arrows */}
                  {mediaItems.length > 1 && (
                    <>
                      <button
                        onClick={prevMedia}
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 backdrop-blur-md rounded-full p-2.5 sm:p-3 z-10 transition-all"
                        style={{ color: '#D5B577' }}
                        aria-label="Previous"
                      >
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                      </button>
                      <button
                        onClick={nextMedia}
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 backdrop-blur-md rounded-full p-2.5 sm:p-3 z-10 transition-all"
                        style={{ color: '#D5B577' }}
                        aria-label="Next"
                      >
                        <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                      </button>
                    </>
                  )}
                  {/* View all / expand for images */}
                  {!(mediaItems[currentMediaIndex].type === 'video' || isVideo(mediaItems[currentMediaIndex].url)) && (
                    <button
                      onClick={() => setIsLightboxOpen(true)}
                      className="absolute bottom-3 right-3 bg-black/50 hover:bg-black/70 backdrop-blur-md rounded-full px-3 py-2 flex items-center gap-2 text-white text-sm z-10"
                      style={{ color: '#D5B577' }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                      {t('common.photo')} {currentMediaIndex + 1} / {mediaItems.length}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Thumbnail strip: vertical on desktop, horizontal on mobile */}
            {mediaItems.length > 1 && (
              <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-y-auto lg:overflow-x-visible pb-2 lg:pb-0 scrollbar-hide flex-shrink-0 lg:w-[120px] lg:max-h-[420px]" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}>
                {mediaItems.map((item, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setCurrentMediaIndex(index)}
                    className={`flex-shrink-0 relative rounded-xl overflow-hidden transition-all border-2 ${
                      index === currentMediaIndex ? 'ring-2 ring-[#D5B577] border-[#D5B577] scale-[0.98]' : 'border-transparent opacity-80 hover:opacity-100'
                    }`}
                    style={{ width: '100px', height: '68px', minWidth: '100px' }}
                  >
                    {item.type === 'video' || isVideo(item.url) ? (
                      <>
                        {property.image || images[0] ? (
                          <img src={property.image || images[0]} alt="" className="w-full h-full object-cover" />
                        ) : null}
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <svg className="w-6 h-6" style={{ color: '#D5B577' }} fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        </div>
                      </>
                    ) : (
                      <img src={item.url} alt={`${property.title} ${index + 1}`} className="w-full h-full object-cover" />
                    )}
                    {index === currentMediaIndex && <div className="absolute inset-0 bg-[#D5B577]/25 pointer-events-none" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-20 sm:top-24 left-4 sm:left-6 lg:left-8 bg-white/20 backdrop-blur-md rounded-full p-2 sm:p-3 hover:bg-white/30 transition-all text-white touch-manipulation z-20"
          aria-label="Go back"
        >
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
      </section>

      {/* Full-screen Lightbox */}
      {isLightboxOpen && mediaItems.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setIsLightboxOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Media gallery"
        >
          <button
            onClick={() => setIsLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white z-10"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/90 text-sm font-medium z-10">
            {currentMediaIndex + 1} / {mediaItems.length}
          </div>
          <div className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={prevMedia}
              className="p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
              style={{ color: '#D5B577' }}
              aria-label="Previous"
            >
              <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
          </div>
          <div className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={nextMedia}
              className="p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20 text-white"
              style={{ color: '#D5B577' }}
              aria-label="Next"
            >
              <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
          <div className="w-full h-full flex items-center justify-center p-4 pt-14 pb-12" onClick={(e) => e.stopPropagation()}>
            {mediaItems[currentMediaIndex].type === 'video' || isVideo(mediaItems[currentMediaIndex].url) ? (
              <div className="w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden">
                {mediaItems[currentMediaIndex].url.includes('youtube.com') || mediaItems[currentMediaIndex].url.includes('youtu.be') || mediaItems[currentMediaIndex].url.includes('vimeo.com') ? (
                  <iframe
                    src={getVideoEmbedUrl(mediaItems[currentMediaIndex].url)}
                    className="w-full h-full"
                    allowFullScreen
                    title={property.title}
                  />
                ) : (
                  <video src={mediaItems[currentMediaIndex].url} className="w-full h-full object-contain" controls autoPlay playsInline />
                )}
              </div>
            ) : (
              <img
                src={mediaItems[currentMediaIndex].url}
                alt={property.title}
                className="max-w-full max-h-full object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        </div>
      )}

      {/* Partial package error modal – when user presses Reserve with partial package */}
      {showPartialPackageModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
          onClick={() => setShowPartialPackageModal(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="partial-package-modal-title"
        >
          <div
            className="rounded-2xl max-w-md w-full p-6 shadow-2xl border border-red-400/40 bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h2 id="partial-package-modal-title" className="text-xl font-bold text-white">
                {language === 'ar' ? 'لا يمكن حجز جزء من باقة' : 'Cannot book partial package'}
              </h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              {language === 'ar'
                ? 'لا يمكن حجز جزء من باقة. يرجى اختيار إما كل أيام الباقة (من تاريخ البداية إلى تاريخ النهاية كاملة) أو أياماً خارج الباقات فقط. مثال: إذا كانت الباقة من 1 إلى 5 شباط، يجب الحجز من 1 إلى 5 فقط وليس من 1 إلى 2 أو من 2 إلى 4.'
                : 'You cannot book a partial package. Please select either the full package dates (from package start to package end) or only dates outside any package. For example, if the package is 1 Feb to 5 Feb, you must book 1 Feb to 5 Feb only — not 1 to 2 or 2 to 4.'}
            </p>
            <button
              type="button"
              onClick={() => setShowPartialPackageModal(false)}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-[#D5B577] text-[#0f172a] hover:bg-[#e0c489] transition-colors"
            >
              {language === 'ar' ? 'حسناً' : 'OK'}
            </button>
          </div>
        </div>
      )}

      {/* Property Details */}
      <section className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#1D2A3B', width: '100%', minHeight: 'auto', overflowX: 'hidden' }}>
        <div className="w-full max-w-7xl mx-auto" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            {/* Main Content */}
            <div className="lg:col-span-3" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
              {/* Title and Location */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h1 className="text-3xl md:text-4xl font-bold text-white">{property.title}</h1>
                  <div className="flex items-center gap-2">
                    {property.code && (
                      <span className="text-sm px-3 py-1.5 rounded-lg font-semibold" style={{ backgroundColor: 'rgba(213, 181, 119, 0.2)', color: '#D5B577' }}>
                        {property.code}
                      </span>
                    )}
                    {property.category && (
                      <span className="text-sm px-3 py-1.5 rounded-lg font-semibold" style={{ backgroundColor: 'rgba(213, 181, 119, 0.15)', color: '#D5B577', border: '1px solid rgba(213, 181, 119, 0.3)' }}>
                        {property.category === 'compound' ? t('common.inCompound') : t('common.separate')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{property.address || property.location}</span>
                </div>
              </div>

              {/* Property Info */}
              <div className="flex flex-wrap gap-6 mb-8 pb-8 border-b" style={{ borderColor: 'rgba(213, 181, 119, 0.2)' }}>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span className="text-gray-300">{property.beds || property.bedrooms} {t('common.beds')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                  </svg>
                  <span className="text-gray-300">{property.baths || property.bathrooms} {t('common.baths')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                  <span className="text-gray-300">{property.sqft ? Math.round(property.sqft * 0.092903) : 'N/A'} m²</span>
                </div>
                {property.type && (
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 rounded-full text-sm" style={{ backgroundColor: 'rgba(213, 181, 119, 0.2)', color: '#D5B577' }}>
                      {property.type}
                    </span>
                  </div>
                )}
              </div>

              {/* Booking Card - Mobile View */}
              <div className="lg:hidden mb-8">
                <div
                  className="rounded-2xl overflow-hidden"
                  style={{
                    background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
                    border: '1px solid rgba(148, 163, 184, 0.15)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                  }}
                >
                  <div className="px-5 pt-5 pb-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-semibold text-white tracking-tight">{formatPrice(property.price, false)}</span>
                      <span className="text-slate-400 text-sm font-medium">/ night</span>
                    </div>
                  </div>

                  <form onSubmit={handleBooking} className="p-5 pt-4">
                    {error && (
                      <div className="mb-4 px-4 py-3 rounded-xl bg-red-500/10 border border-red-400/30 text-red-200 text-sm font-medium">
                        {error}
                      </div>
                    )}

                    <div className="mb-5">
                      <label className="block text-slate-400 text-xs font-medium uppercase tracking-wider mb-2">
                        {language === 'ar' ? 'التواريخ' : 'Dates'}
                      </label>
                      <DateFieldWithCalendar
                        checkIn={bookingForm.checkIn}
                        checkOut={bookingForm.checkOut}
                        onChange={(checkIn, checkOut) => {
                          setBookingForm((prev) => ({ ...prev, checkIn: checkIn || prev.checkIn, checkOut: checkOut !== undefined ? checkOut : prev.checkOut }));
                          if (error) setError('');
                        }}
                        blockedDates={blockedDates}
                        packages={property.packages || []}
                        language={language}
                        placeholder={language === 'ar' ? 'اختر الوصول والمغادرة' : 'Check-in — Check-out'}
                        placeholderAr="اختر الوصول والمغادرة"
                        monthsAhead={12}
                      />
                    </div>

                    <div className="mb-5">
                      <label className="block text-slate-400 text-xs font-medium uppercase tracking-wider mb-2">
                        {t('propertyDetail.guests')}
                      </label>
                      {property?.maxGuests != null && (
                        <p className="text-slate-400 text-xs mb-1.5">
                          {language === 'ar' ? 'السعر الأساسي يشمل حتى ' : 'Base rate includes up to '}
                          <span className="text-white font-medium">{property.maxGuests}</span>
                          {language === 'ar' ? ' نزيلاً' : ' guests'}
                        </p>
                      )}
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={bookingForm.guests}
                        onChange={(e) => setBookingForm((prev) => ({ ...prev, guests: parseInt(e.target.value) || 1 }))}
                        className="w-full rounded-xl h-11 px-4 text-white text-sm font-medium bg-slate-700/60 border border-slate-600/80 focus:border-[#D5B577] focus:ring-1 focus:ring-[#D5B577]/30 outline-none transition-all"
                      />
                      {Number(property?.extraFeesPerPerson) > 0 && (
                        <p className="text-slate-500 text-xs mt-1.5">
                          {language === 'ar' ? 'إضافة شخص إضافي تضيف تكلفة: ' : 'Adding an extra person adds a cost: '}
                          {formatPrice(Number(property.extraFeesPerPerson))}
                          {language === 'ar' ? ' لكل شخص إضافي' : ' per additional person'}
                        </p>
                      )}
                    </div>

                    {nights > 0 && (
                      <div className="mb-5 pt-4 border-t border-slate-700/80 space-y-2">
                        <div className="flex justify-between items-baseline">
                          <span className="text-slate-400 text-sm">{nights} {language === 'ar' ? 'ليلة' : 'nights'}</span>
                          <span className="text-white font-medium">{formatPrice(basePrice)}</span>
                        </div>
                        <div className="flex justify-between items-baseline text-slate-400 text-sm">
                          <span>{guestsCount} {language === 'ar' ? 'نزيل' : 'guests'}</span>
                          <span className="text-white">{extraGuestsFee > 0 ? `+${formatPrice(extraGuestsFee)}` : formatPrice(0)}</span>
                        </div>
                        <div className="flex justify-between items-baseline pt-1 border-t border-slate-700/50">
                          <span className="text-slate-300 text-sm font-medium">{language === 'ar' ? 'المجموع' : 'Total'}</span>
                          <span className="text-[#D5B577] font-semibold text-lg">{formatPrice(totalPrice)}</span>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full h-12 rounded-xl text-sm font-semibold text-[#0f172a] bg-[#D5B577] hover:bg-[#e0c489] active:scale-[0.99] transition-all duration-200 shadow-lg shadow-[#D5B577]/20"
                    >
                      {user ? (language === 'ar' ? 'احجز الآن' : 'Reserve') : t('propertyDetail.signInToBook')}
                    </button>
                  </form>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-4">{t('common.aboutProperty')}</h2>
                <p className="text-gray-300 leading-relaxed">{property.description}</p>
              </div>

              {/* Features */}
              {property.features && property.features.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white mb-4">{t('common.whatPlaceOffers')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {property.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <svg className="w-5 h-5" style={{ color: '#D5B577' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Location Map */}
              {(property.mapLink || property.address || property.location) && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white mb-4">{t('common.whereYoullBe')}</h2>
                  <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'rgba(213, 181, 119, 0.3)' }}>
                    {(() => {
                      // Helper function to convert Google Maps share link to embed URL (without API key)
                      const getEmbedUrl = (mapLink) => {
                        if (!mapLink) return '';
                        
                        // If it's already an embed URL with maps/embed, use it directly (but remove API key if present)
                        if (mapLink.includes('maps/embed')) {
                          // Remove API key parameter if present
                          return mapLink.replace(/[?&]key=[^&]*/, '').replace(/[?&]key=[^&]*/, '');
                        }
                        
                        // Extract coordinates from URL (format: @lat,lng)
                        const coordMatch = mapLink.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
                        if (coordMatch) {
                          const lat = coordMatch[1];
                          const lng = coordMatch[2];
                          // Use coordinates to create embed URL without API key
                          return `https://www.google.com/maps?q=${lat},${lng}&output=embed`;
                        }
                        
                        // Extract place name from /place/ URL
                        if (mapLink.includes('/place/')) {
                          const placeMatch = mapLink.match(/\/place\/([^/?]+)/);
                          if (placeMatch) {
                            const placeName = decodeURIComponent(placeMatch[1].replace(/\+/g, ' '));
                            return `https://www.google.com/maps?q=${encodeURIComponent(placeName)}&output=embed`;
                          }
                        }
                        
                        // Try to extract search query parameter
                        const searchMatch = mapLink.match(/[?&]q=([^&]+)/);
                        if (searchMatch) {
                          const query = decodeURIComponent(searchMatch[1]);
                          return `https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`;
                        }
                        
                        // Fallback: use address/location or the mapLink itself as search query
                        const searchQuery = property.address || property.location || mapLink;
                        return `https://www.google.com/maps?q=${encodeURIComponent(searchQuery)}&output=embed`;
                      };
                      
                      // Use mapLink if available, otherwise create one from address/location
                      const mapLinkToUse = property.mapLink || (property.address || property.location ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address || property.location)}` : '');
                      const embedUrl = getEmbedUrl(mapLinkToUse);
                      
                      if (!embedUrl) return null;
                      
                      return (
                        <iframe
                          src={embedUrl}
                          width="100%"
                          height="400"
                          style={{ border: 0 }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title={`Map of ${property.title}`}
                        ></iframe>
                      );
                    })()}
                  </div>
                  <div className="mt-3">
                    <a
                      href={(property.mapLink && property.mapLink.startsWith('http')) 
                        ? property.mapLink 
                        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address || property.location || property.mapLink || '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm inline-flex items-center gap-2 transition-colors"
                      style={{ color: '#D5B577' }}
                      onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      Open in Google Maps
                    </a>
                  </div>
                </div>
              )}

            </div>

            {/* Booking Card - Desktop View */}
            <div className="hidden lg:block lg:col-span-1 lg:order-last">
              <div className="lg:sticky lg:top-24">
                <div
                  className="rounded-2xl overflow-hidden"
                  style={{
                    background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
                    border: '1px solid rgba(148, 163, 184, 0.15)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                  }}
                >
                  <div className="px-5 pt-5 pb-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-semibold text-white tracking-tight">{formatPrice(property.price, false)}</span>
                      <span className="text-slate-400 text-sm font-medium">/ night</span>
                    </div>
                  </div>

                  <form onSubmit={handleBooking} className="p-5 pt-4">
                    {error && (
                      <div className="mb-4 px-4 py-3 rounded-xl bg-red-500/10 border border-red-400/30 text-red-200 text-sm font-medium">
                        {error}
                      </div>
                    )}

                    <div className="mb-5">
                      <label className="block text-slate-400 text-xs font-medium uppercase tracking-wider mb-2">
                        {language === 'ar' ? 'التواريخ' : 'Dates'}
                      </label>
                      <DateFieldWithCalendar
                        checkIn={bookingForm.checkIn}
                        checkOut={bookingForm.checkOut}
                        onChange={(checkIn, checkOut) => {
                          setBookingForm((prev) => ({ ...prev, checkIn: checkIn || prev.checkIn, checkOut: checkOut !== undefined ? checkOut : prev.checkOut }));
                          if (error) setError('');
                        }}
                        blockedDates={blockedDates}
                        packages={property.packages || []}
                        language={language}
                        placeholder={language === 'ar' ? 'اختر الوصول والمغادرة' : 'Check-in — Check-out'}
                        placeholderAr="اختر الوصول والمغادرة"
                        monthsAhead={12}
                      />
                    </div>

                    <div className="mb-5">
                      <label className="block text-slate-400 text-xs font-medium uppercase tracking-wider mb-2">
                        {t('propertyDetail.guests')}
                      </label>
                      {property?.maxGuests != null && (
                        <p className="text-slate-400 text-xs mb-1.5">
                          {language === 'ar' ? 'السعر الأساسي يشمل حتى ' : 'Base rate includes up to '}
                          <span className="text-white font-medium">{property.maxGuests}</span>
                          {language === 'ar' ? ' نزيلاً' : ' guests'}
                        </p>
                      )}
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={bookingForm.guests}
                        onChange={(e) => setBookingForm((prev) => ({ ...prev, guests: parseInt(e.target.value) || 1 }))}
                        className="w-full rounded-xl h-11 px-4 text-white text-sm font-medium bg-slate-700/60 border border-slate-600/80 focus:border-[#D5B577] focus:ring-1 focus:ring-[#D5B577]/30 outline-none transition-all"
                      />
                      {Number(property?.extraFeesPerPerson) > 0 && (
                        <p className="text-slate-500 text-xs mt-1.5">
                          {language === 'ar' ? 'إضافة شخص إضافي تضيف تكلفة: ' : 'Adding an extra person adds a cost: '}
                          {formatPrice(Number(property.extraFeesPerPerson))}
                          {language === 'ar' ? ' لكل شخص إضافي' : ' per additional person'}
                        </p>
                      )}
                    </div>

                    {nights > 0 && (
                      <div className="mb-5 pt-4 border-t border-slate-700/80 space-y-2">
                        <div className="flex justify-between items-baseline">
                          <span className="text-slate-400 text-sm">{nights} {language === 'ar' ? 'ليلة' : 'nights'}</span>
                          <span className="text-white font-medium">{formatPrice(basePrice)}</span>
                        </div>
                        <div className="flex justify-between items-baseline text-slate-400 text-sm">
                          <span>{guestsCount} {language === 'ar' ? 'نزيل' : 'guests'}</span>
                          <span className="text-white">{extraGuestsFee > 0 ? `+${formatPrice(extraGuestsFee)}` : formatPrice(0)}</span>
                        </div>
                        <div className="flex justify-between items-baseline pt-1 border-t border-slate-700/50">
                          <span className="text-slate-300 text-sm font-medium">{language === 'ar' ? 'المجموع' : 'Total'}</span>
                          <span className="text-[#D5B577] font-semibold text-lg">{formatPrice(totalPrice)}</span>
                        </div>
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full h-12 rounded-xl text-sm font-semibold text-[#0f172a] bg-[#D5B577] hover:bg-[#e0c489] active:scale-[0.99] transition-all duration-200 shadow-lg shadow-[#D5B577]/20"
                    >
                      {user ? (language === 'ar' ? 'احجز الآن' : 'Reserve') : t('propertyDetail.signInToBook')}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <WhatsAppButton phoneNumber="+1234567890" message="Hello! I'm interested in this property." />
    </div>
  );
};

export default PropertyDetail;

